import {
  Autocomplete,
  Avatar,
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "../../hooks/useDebounce.js";
import CloseIcon from "@mui/icons-material/Close";
import { stringToColor } from "../../utils/utils.js";
import FollowButton, {
  useFollowToggle,
} from "../../components/FollowButton.js";

export default function UserSearch({ fullscreen, onClose }) {
  const { fetchUserSearch } = useApi();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearch = useDebounce(async (query) => {
    try {
      setIsLoading(true);
      const results = await fetchUserSearch(query);
      if (results.length > 0) {
        setOptions(results);
      } else {
        setOptions([{ noResults: true, label: "No profiles found" }]);
      }
    } catch (error) {
      console.error("Failed to fetch search results");
    } finally {
      setIsLoading(false);
    }
  }, 300);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleInputChange = async (event, value) => {
    setInput(value);
    if (value.trim().length > 0) {
      debouncedSearch(value);
    } else {
      setOptions([]);
      debouncedSearch.cancel();
    }
  };

  const handleSelect = (event, value) => {
    const username = value?.username;
    if (!username) return;
    onClose?.();
    navigate(`/u/${value.username}`);
  };

  function stringAvatar(name) {
    if (!name) return { children: "?" }; // Default fallback if no name is available

    const nameParts = name.trim().split(" ").filter(Boolean); // Ensure no empty parts

    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children:
        nameParts.length > 1
          ? `${nameParts[0][0]}${nameParts[1][0].toUpperCase()}` // First letters of first & last name
          : `${nameParts[0][0].toUpperCase()}`, // Only first letter if single word
    };
  }

  const { isFollowing, toggleFollow } = useFollowToggle();

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="body2">Search profile...</Typography>
        <Box display="flex" alignItems="center">
          {fullscreen && onClose && (
            <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
              {" "}
              <CloseIcon />{" "}
            </IconButton>
          )}
        </Box>
      </Stack>

      <Autocomplete
        fullWidth
        options={options}
        clearOnEscape
        onInputChange={handleInputChange}
        onChange={handleSelect}
        inputValue={input}
        loading={isLoading}
        loadingText="Searching..."
        filterOptions={(x) => (x.length === 0 ? [] : x)}
        getOptionLabel={(option) => {
          if (option.noResults) {
            return option.label;
          }
          return option.username || "";
        }}
        renderOption={(props, option) => {
          if (option.noResults) {
            return (
              <li {...props} style={{ pointerEvents: "none" }}>
                <Typography fontWeight="bold">{option.label}</Typography>
              </li>
            );
          }
          return (
            <li {...props} key={option.username}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar
                    {...stringAvatar(option.fullName || option.username)}
                  />
                  <Typography
                    variant={fullscreen ? "body2" : "body1"}
                    sx={{ fontStyle: "italic" }}
                  >
                    @{option.username}
                  </Typography>
                  {!fullscreen && (
                    <Typography variant="body2" color="text.disabled">
                      {option.fullName}
                    </Typography>
                  )}
                </Stack>
                <FollowButton
                  following={isFollowing(option)}
                  username={option.username}
                  iconOnly={fullscreen}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleFollow(option);
                  }}
                />
              </Stack>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            hiddenLabel={true}
            size="small"
            variant="filled"
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
            }}
          />
        )}
      />
    </Box>
  );
}
